import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Calendar } from 'lucide-react';
import { ProgramFormData, DayPlan } from '../../types/program';
import DayPlanEditor from './components/DayPlanEditor';
import CustomSelect from '../../components/CustomSelect';
import { programService } from '../../services/programService';
import { showToast } from '../../components/Toast';
import NutritionPlanTab from './components/NutritionPlanTab';
import RecoveryProtocolTab from './components/RecoveryProtocolTab';
import ShoppingListTab from './components/ShoppingListTab';
import { getFullMediaUrl } from '../../utils/urlHelper';

const ProgramArchitect: React.FC = () => {
  const navigate = useNavigate();
  const { id: programId } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'info' | 'workouts' | 'nutrition' | 'recovery' | 'shopping'>('info');
  
  const [formData, setFormData] = useState<ProgramFormData>({
    title: '',
    description: '',
    difficulty: 'beginner',
    durationWeeks: 4,
    tags: [],
    imageUrl: '',
    versions: [{
      versionNumber: '1.0',
      days: [],
    }],
    nutritionPlan: {
      macroTargets: { calories: 0, protein: 0, carbs: 0, fats: 0 },
      meals: [],
    },
    recoveryProtocol: {
      items: [],
    },
    shoppingList: {
      weekNumber: 1,
      items: [],
    },
  });

  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);

  // Load existing program data for edit mode
  useEffect(() => {
    const loadProgramData = async () => {
      setIsLoading(true);
      try {
        const program = await programService.getProgramById(programId!);
        setFormData({
          title: program.title,
          description: program.description,
          difficulty: program.difficulty,
          durationWeeks: program.durationWeeks,
          tags: program.tags,
          imageUrl: program.imageUrl,
          versions: program.versions,
          nutritionPlan: program.nutritionPlan || {
            macroTargets: { calories: 0, protein: 0, carbs: 0, fats: 0 },
            meals: [],
          },
          recoveryProtocol: program.recoveryProtocol || {
            items: [],
          },
          shoppingList: program.shoppingList || {
            weekNumber: 1,
            items: [],
          },
        });
      } catch (error) {
        console.error('Error loading program:', error);
        showToast('Failed to load program data', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (programId && programId !== 'create') {
      loadProgramData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  const handleInputChange = (field: keyof ProgramFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const handleAddDay = () => {
    const newDay: DayPlan = {
      dayNumber: formData.versions[0].days.length + 1,
      name: `Day ${formData.versions[0].days.length + 1}`,
      exercises: [],
    };

    setFormData(prev => ({
      ...prev,
      versions: [{
        ...prev.versions[0],
        days: [...prev.versions[0].days, newDay],
      }],
    }));
  };

  const handleUpdateDay = (dayIndex: number, updatedDay: DayPlan) => {
    setFormData(prev => ({
      ...prev,
      versions: [{
        ...prev.versions[0],
        days: prev.versions[0].days.map((day, index) =>
          index === dayIndex ? updatedDay : day
        ),
      }],
    }));
  };

  const handleDeleteDay = (dayIndex: number) => {
    setFormData(prev => {
      const updatedDays = prev.versions[0].days.filter((_, index) => index !== dayIndex);
      // Renumber remaining days
      const renumberedDays = updatedDays.map((day, index) => ({
        ...day,
        dayNumber: index + 1,
      }));

      return {
        ...prev,
        versions: [{
          ...prev.versions[0],
          days: renumberedDays,
        }],
      };
    });
  };

  const handleCoverImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Please select a valid image file (JPG, PNG, or GIF)', 'error');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image size must be less than 5MB', 'error');
      return;
    }

    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Show loading state
    setIsSaving(true);
    try {
      const imageUrl = await programService.uploadCoverImage(file);
      // Keep the preview and update imageUrl
      setFormData(prev => ({
        ...prev,
        imageUrl,
      }));
    } catch (error: any) {
      console.error('Error uploading cover image:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to upload cover image';
      showToast(errorMessage, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.title || formData.title.trim() === '') {
      showToast('Please enter a program title', 'error');
      return;
    }

    if (formData.title.length < 3) {
      showToast('Title must be at least 3 characters long', 'error');
      return;
    }

    if (!formData.description || formData.description.trim() === '') {
      showToast('Please enter a program description', 'error');
      return;
    }

    if (!formData.difficulty) {
      showToast('Please select a difficulty level', 'error');
      return;
    }

    if (!formData.durationWeeks || formData.durationWeeks < 1) {
      showToast('Please enter a valid duration (at least 1 week)', 'error');
      return;
    }

    if (formData.versions[0].days.length === 0) {
      showToast('Please add at least one workout day', 'error');
      return;
    }

    // Check if any day has no exercises
    const daysWithExercises = formData.versions[0].days.filter(day => day.exercises.length > 0);
    if (daysWithExercises.length === 0) {
      showToast('Please add at least one exercise to your workout days', 'error');
      return;
    }

    setIsSaving(true);
    try {
      if (programId) {
        // Update existing program
        await programService.updateProgram(programId, formData);
      } else {
        // Create new program
        await programService.createProgram(formData);
      }
      
      // Navigate back to Program Manager
      navigate('/programs');
    } catch (error: any) {
      console.error('Error saving program:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to save program. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'info' as const, label: 'Program Info' },
    { id: 'workouts' as const, label: 'Workout Days' },
    { id: 'nutrition' as const, label: 'Nutrition' },
    { id: 'recovery' as const, label: 'Recovery' },
    { id: 'shopping' as const, label: 'Shopping List' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1F1F1F] border-t-[#23F47C] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#8F8F8F]">Loading program...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1920px] mx-auto">
      {/* Header - Height: 80px, Padding: 24px horizontal */}
      <div className="flex items-center justify-between h-20 px-6 border-b border-[#1F1F1F]">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/programs')}
            className="w-9 h-9 bg-transparent border border-[#1F1F1F] rounded-lg flex items-center justify-center text-[#8F8F8F] hover:bg-[#1F1F1F] hover:text-white transition-all duration-200"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-[32px] font-bold text-white leading-[40px] tracking-[-0.5px]">
            {programId ? 'Edit Program' : 'Create Program'}
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/programs')}
            className="h-11 px-6 bg-transparent border border-[#1F1F1F] text-white text-base font-semibold rounded-lg hover:bg-[#141414] hover:border-[#23F47C] hover:text-[#23F47C] transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 h-11 px-6 bg-[#23F47C] text-[#0A0A0A] text-base font-semibold rounded-lg hover:bg-[#1FD66D] disabled:bg-[#1F1F1F] disabled:text-[#5F5F5F] disabled:cursor-not-allowed transition-all duration-200"
          >
            <Save size={18} />
            <span>{isSaving ? 'Saving...' : 'Save Program'}</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation - Height: 56px, Border-bottom: 2px solid #1F1F1F */}
      <div className="flex gap-2 px-6 border-b-2 border-[#1F1F1F]">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`h-14 px-5 text-[15px] font-semibold border-b-2 transition-all duration-200 relative -mb-0.5 ${
              activeTab === tab.id
                ? 'text-[#23F47C] border-[#23F47C]'
                : 'text-[#8F8F8F] border-transparent hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content - Padding: 32px 24px */}
      <div className="px-6 py-8 min-h-[calc(100vh-136px)]">
        {activeTab === 'info' && (
          <div className="max-w-4xl">
            <h2 className="text-2xl font-bold text-white mb-6">Program Information</h2>
            
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Program Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter program title"
                  className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder-[#5F5F5F] focus:border-[#23F47C] focus:outline-none focus:shadow-[0_0_0_3px_rgba(35,244,124,0.1)] transition-all duration-200"
                />
                <p className="text-xs text-[#8F8F8F] mt-1.5">
                  Minimum 8 characters
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe your program"
                  rows={4}
                  className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder-[#5F5F5F] focus:border-[#23F47C] focus:outline-none focus:shadow-[0_0_0_3px_rgba(35,244,124,0.1)] transition-all duration-200"
                />
              </div>

              {/* Difficulty and Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Difficulty *
                  </label>
                  <CustomSelect
                    value={formData.difficulty}
                    onChange={(value) => handleInputChange('difficulty', value)}
                    options={[
                      { value: 'beginner', label: 'Beginner' },
                      { value: 'intermediate', label: 'Intermediate' },
                      { value: 'advanced', label: 'Advanced' },
                    ]}
                    placeholder="Select difficulty"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Duration (weeks) *
                  </label>
                  <input
                    type="number"
                    value={formData.durationWeeks}
                    onChange={(e) => handleInputChange('durationWeeks', parseInt(e.target.value) || 1)}
                    placeholder="4"
                    min="1"
                    max="52"
                    className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder-[#5F5F5F] focus:border-[#23F47C] focus:outline-none focus:shadow-[0_0_0_3px_rgba(35,244,124,0.1)] transition-all duration-200"
                  />
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Tags
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Add a tag"
                    className="flex-1 px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder-[#5F5F5F] focus:border-[#23F47C] focus:outline-none focus:shadow-[0_0_0_3px_rgba(35,244,124,0.1)] transition-all duration-200"
                  />
                  <button
                    onClick={handleAddTag}
                    className="h-11 px-6 bg-[#1F1F1F] text-white text-sm font-semibold rounded-lg hover:bg-[#23F47C] hover:text-[#0A0A0A] transition-all duration-200"
                  >
                    Add
                  </button>
                </div>
                
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-[rgba(35,244,124,0.1)] text-[#23F47C] rounded-lg text-sm font-medium"
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="text-[#23F47C] hover:text-[#1FD66D]"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Cover Image */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Cover Image
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-32 h-32 bg-[#141414] border border-[#1F1F1F] rounded-lg flex items-center justify-center overflow-hidden">
                    {coverImagePreview ? (
                      <img
                        src={coverImagePreview}
                        alt="Cover preview"
                        className="w-full h-full object-cover"
                      />
                    ) : formData.imageUrl ? (
                      <img
                        src={getFullMediaUrl(formData.imageUrl) || ''}
                        alt="Cover preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-[#5F5F5F] text-sm">No image</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleCoverImageUpload}
                      className="block w-full text-sm text-[#8F8F8F] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#23F47C] file:text-[#0A0A0A] hover:file:bg-[#1FD66D] cursor-pointer"
                    />
                    <p className="text-xs text-[#8F8F8F] mt-1.5">
                      Supported formats: JPG, PNG, GIF (max 5MB)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'workouts' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Workout Days</h2>
                <p className="text-[#8F8F8F]">
                  {formData.versions[0].days.length} of {formData.durationWeeks * 7} days configured
                </p>
              </div>
              <button
                onClick={handleAddDay}
                className="inline-flex items-center gap-2 h-11 px-6 bg-[#23F47C] text-[#0A0A0A] text-base font-semibold rounded-lg hover:bg-[#1FD66D] transition-all duration-200"
              >
                <Plus size={18} />
                <span>Add Day</span>
              </button>
            </div>

            {formData.versions[0].days.length === 0 ? (
              <div className="bg-[#141414] rounded-2xl border border-[#1F1F1F] flex flex-col items-center justify-center min-h-[400px] px-6 py-12">
                <Calendar size={64} className="text-[#5F5F5F] mb-6" />
                <h3 className="text-white text-xl font-bold mb-2">No Workout Days Yet</h3>
                <p className="text-[#8F8F8F] text-sm text-center max-w-md mb-6">
                  Start building your program by adding workout days
                </p>
                <button
                  onClick={handleAddDay}
                  className="inline-flex items-center gap-2 h-11 px-6 bg-[#23F47C] text-[#0A0A0A] text-base font-semibold rounded-lg hover:bg-[#1FD66D] transition-all duration-200"
                >
                  <Plus size={18} />
                  <span>Add First Day</span>
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.versions[0].days.map((day, dayIndex) => (
                  <DayPlanEditor
                    key={dayIndex}
                    dayPlan={day}
                    dayIndex={dayIndex}
                    onUpdate={handleUpdateDay}
                    onDelete={handleDeleteDay}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'nutrition' && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Nutrition Plan</h2>
            <p className="text-[#8F8F8F] mb-6">
              Add nutrition guidelines and meal plans for your program.
            </p>
            <NutritionPlanTab
              nutritionPlan={formData.nutritionPlan || null}
              onUpdate={(plan) => setFormData(prev => ({ ...prev, nutritionPlan: plan }))}
            />
          </div>
        )}

        {activeTab === 'recovery' && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Recovery Protocol</h2>
            <p className="text-[#8F8F8F] mb-6">
              Define rest days, stretching routines, and recovery techniques.
            </p>
            <RecoveryProtocolTab
              recoveryProtocol={formData.recoveryProtocol || null}
              onUpdate={(protocol) => setFormData(prev => ({ ...prev, recoveryProtocol: protocol }))}
            />
          </div>
        )}

        {activeTab === 'shopping' && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Shopping List</h2>
            <p className="text-[#8F8F8F] mb-6">
              Add recommended equipment, supplements, and apparel for this program.
            </p>
            <ShoppingListTab
              shoppingList={formData.shoppingList || null}
              onUpdate={(list) => setFormData(prev => ({ ...prev, shoppingList: list }))}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramArchitect;
